import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { useRouter } from 'next/router';
import { isAfter } from 'date-fns';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'next-i18next';

import { signInModalInstance } from 'components/candidate/SignInModal';
import { applyConfirmationModalInstance } from 'components/candidate/ApplyConfirmationModal';
import { externalApplyModalInstance } from 'components/candidate/ExternalApplyModal';
import { registrationDetailsModalInstance } from 'components/candidate/RegistrationDetailsModal';
import { externalApplySignupModalInstance } from 'components/candidate/ExternalApplySignup';
import { notifyUserModalInstance } from 'components/candidate/NotifyUserModal';
import { nonOnboardedUserRegistrationSuccessModalInstance } from 'components/candidate/NonOnboardedUserRegistrationSuccessModal';
import { joinMemberModalInstance } from 'components/candidate/JoinMemberModal';
import { TouchpointNotifications, NotificationType } from 'components/candidate/Notifications/TouchpointNotifications.component';
import { useCommon } from 'hooks/useCommon';
import { FLAG_STATUS_CODES, useCandidate } from 'hooks/useCandidate';
import { OpportunitiesResponse, OpportunityResponse, TOpportunity, TouchpointState } from 'lib/models/opportunity';
import { apiInstance } from 'lib/utils/axios';
import { API_ROUTES } from 'lib/api-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { FollowingAttributes } from 'lib/models/following';
import { TouchpointType } from 'lib/models/touchpoint';
import { EventTouchpointable } from 'lib/models/event';
import { CompanyResponse, TCompany } from 'lib/models/company';
import { StudentSocietyResponse } from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseOpportunityAiFaqs } from 'utils/opportunity';

type SocietySubscription = { subscribed: boolean; awaiting_approval: boolean };

type OpportunityAttributesUpdateRecord = {
  following?: TOpportunity['following'];
  is_applied?: boolean;
  is_waitlisted?: boolean;
  creatable_for_following?: TCompany['following'];
  creatable_for_subscription?: SocietySubscription;
  cohost_following?: { cohostId?: string; followingId?: string; following: TCompany['following'] };
  cohost_subscription?: { cohostId: string; subscription: SocietySubscription };
};

export enum UserActionEnum {
  SAVE = 'save',
  UNSAVE = 'unsave',
  APPLY = 'apply',
  REGISTER = 'register',
  FOLLOW_COMPANY = 'follow_company',
  UNFOLLOW_COMPANY = 'unfollow_company',
  JOIN_SOCIETY = 'join_society',
  DISJOIN_SOCIETY = 'disjoin_society',
  APPLY_CONFIRMATION = 'apply_confirmation',
  REDIRECT_TO = 'redirect_to',
}

type UpdateUserActionProps =
  | {
      userAction: UserActionEnum.FOLLOW_COMPANY | UserActionEnum.UNFOLLOW_COMPANY | UserActionEnum.JOIN_SOCIETY | UserActionEnum.DISJOIN_SOCIETY;
      userActionId: string;
    }
  | {
      userAction:
        | UserActionEnum.APPLY
        | UserActionEnum.REGISTER
        | UserActionEnum.SAVE
        | UserActionEnum.UNSAVE
        | UserActionEnum.APPLY_CONFIRMATION
        | null;
    }
  | {
      userAction: UserActionEnum.REDIRECT_TO;
      userActionURL: string;
    };

export function useOpportunityInternalHook({
  opportunity: staticPropsOpportunity,
  isLoggedInData,
  isOpportunityPublicPage,
  isMatchesPage,
  matchSlug,
  isSearchPage,
  isPageFullyScrolled,
}: {
  opportunity: TOpportunity;
  isOpportunityPublicPage: boolean;
  isMatchesPage: boolean;
  isLoggedInData: boolean;
  matchSlug?: string;
  isSearchPage: boolean;
  isPageFullyScrolled: boolean;
}) {
  const [t] = useTranslation('opportunity');
  const { replace, push, query } = useRouter();
  const { onApplyClick, applyOpportunity, sendInvalidURLReport, onShare, followEntity, unfollowEntity, onJoinSociety, onDisjoinSociety } =
    useCommon();
  const { action: queryUserAction = null, action_id: queryUserActionId = null, action_url: queryActionUrl = null } = query;
  const { id: opportunityId, public_url, apply_url } = staticPropsOpportunity;
  const { isCandidate, isLoggedIn, isNonOnboardedUser, isInitialAuthLoading, candidate, setIsNewCandidate } = useAuth();
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const opportunityPath = `${API_ROUTES.TOUCHPOINTS}/${opportunityId}`;
  const shouldFetchOpportunity = isCandidate && !isLoggedInData;
  const shouldFetchOpportunityRef = useRef(shouldFetchOpportunity);

  const touchpointNotificationInstance = TouchpointNotifications();
  const { updateCount } = useCandidate();
  const {
    first_name,
    last_name,
    company_follow_count,
    student_society_follow_count,
    saved_event_count,
    saved_non_event_count,
    external_registrated_event_count,
    external_applied_non_event_count,
  } = candidate || {};

  // Added this because when guest user gets logged in using registrationDetailsModalInstance and the application is in progress and the userAction is not null it which triggers applyInternalJob again because isCandidate is true
  const internalJobApplicationInProgressRef = useRef(false);

  const opportunityFallbackData = useMemo(() => {
    return {
      data: { data: { id: staticPropsOpportunity.id, type: 'touchpoint', attributes: { ...staticPropsOpportunity } } },
    } as AxiosResponse<OpportunityResponse>;
  }, [staticPropsOpportunity]);

  const { data: opportunityResponse, isLoading: isOpportunityLoading } = useSWR<AxiosResponse<OpportunityResponse>>(
    opportunityPath,
    shouldFetchOpportunity ? apiInstance.get : null,
    {
      fallbackData: opportunityFallbackData,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (shouldFetchOpportunityRef.current === false && shouldFetchOpportunity) {
      shouldFetchOpportunityRef.current = shouldFetchOpportunity;
      mutate(opportunityPath);
    }
  }, [shouldFetchOpportunityRef, shouldFetchOpportunity, opportunityPath]);

  const { state, application_deadline, touchpointable: touchpointableResponse, touchpointable_type } = staticPropsOpportunity;
  const isExpired = state === TouchpointState.EXPIRED;
  const isApplicationClosed = application_deadline ? isAfter(new Date(), new Date(application_deadline)) : false;
  const isEvent = touchpointable_type === TouchpointType.Event;
  const isEventEnded = useMemo(() => {
    if (touchpointable_type !== TouchpointType.Event) return false;
    const touchpointable = parseResponse(touchpointableResponse as EventTouchpointable);
    return touchpointable.end_date ? isAfter(new Date(), new Date(touchpointable.end_date)) : false;
  }, [touchpointableResponse, touchpointable_type]);

  const opportunity = useMemo(() => {
    return parseResponse<TOpportunity>(opportunityResponse?.data);
  }, [opportunityResponse]);

  const similarOpportunitiesPathname = `${API_ROUTES.CAMPAIGNS}/${opportunity.slug}/similar?enabled=true&state=published`;
  const { data: similarOpportunitiesResponse, isLoading: similarOpportunitiesLoading } = useSWR<AxiosResponse<OpportunitiesResponse>>(
    similarOpportunitiesPathname,
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );
  const similarOpportunities = useMemo(() => parseArrayResponse(similarOpportunitiesResponse?.data), [similarOpportunitiesResponse]);

  const { creatable_for: creatableForResponse, cohost_applications: cohost_applications_response } = opportunity;
  const creatableForType = creatableForResponse.data.type;

  const { creatableFor, awaitingApproval, subscribers_require_approval } = useMemo(() => {
    if (creatableForType === 'company') {
      const company = parseResponse(creatableForResponse as CompanyResponse);
      return { creatableFor: company, awaitingApproval: false, subscribers_require_approval: false };
    } else {
      const society = parseResponse(creatableForResponse as StudentSocietyResponse);
      return {
        creatableFor: society,
        awaitingApproval: society.subscribers_require_approval && society.awaiting_approval,
        subscribers_require_approval: society.subscribers_require_approval,
      };
    }
  }, [creatableForResponse, creatableForType]);

  const creatableForId = creatableFor.id;
  const creatableForName = creatableFor.name;
  const creatableForFollowingId = creatableFor.following?.data?.id;
  const opportunityFollowingId = opportunity.following?.data ? parseResponse<FollowingAttributes>(opportunity.following)?.id : null;
  const isFollowing = !!opportunityFollowingId;
  const eventTouchpointable = isEvent ? parseResponse(opportunity.touchpointable as EventTouchpointable) : null;
  const isWaitlistEnabled = !!eventTouchpointable?.waitlist;
  const isApprovalRequired = !!eventTouchpointable?.enforce_waitlist_for_all;
  const isCapacityReached = eventTouchpointable?.is_capacity_reached;
  const isAlmostSoldOut = eventTouchpointable?.is_almost_sold_out;
  const isOpportunityUnappliable =
    (isCapacityReached && !isWaitlistEnabled && !opportunity.is_applied) || isApplicationClosed || isEventEnded || isExpired;
  const isOpportunityAppliedOrUnappliable = opportunity.is_applied || isOpportunityUnappliable;
  const willJoinWaitlist = isApprovalRequired || (isCapacityReached && isWaitlistEnabled);
  const cohosts = useMemo(() => {
    const cohost_applications = parseArrayResponse(cohost_applications_response);
    return cohost_applications.map((item) => {
      const cohost = parseResponse(item.cohost);
      return { id: cohost.id, name: cohost.name, subscribers_require_approval: cohost.subscribers_require_approval };
    });
  }, [cohost_applications_response]);

  const updateUserAction = useCallback(
    (props: UpdateUserActionProps) => {
      const { userAction } = props;
      if ('userActionId' in props) {
        replace({ query: { ...query, action: userAction, action_id: props.userActionId } }, undefined, { shallow: true });
      } else if ('userActionURL' in props) {
        replace({ query: { ...query, action: userAction, action_url: props.userActionURL } }, undefined, { shallow: true });
      } else {
        if (userAction) {
          replace({ query: { ...query, action: userAction } }, undefined, { shallow: true });
        } else {
          const { action: _, action_id: __, ...restQuery } = query;
          replace({ query: restQuery }, undefined, { shallow: true });
        }
      }
    },
    [replace, query]
  );

  const onUpdateOpportunity = useCallback(
    (record: OpportunityAttributesUpdateRecord) => {
      mutate(
        opportunityPath,
        () => {
          const oldOpportunityResponse = opportunityResponse;
          if (!oldOpportunityResponse) return oldOpportunityResponse;
          const { data: oldOpportunityResponseData } = oldOpportunityResponse;
          const oppportunityAttributes = parseResponse(oldOpportunityResponseData);
          const { creatable_for } = oppportunityAttributes;

          let newOpportunityData: TOpportunity;
          if (typeof record.creatable_for_following !== 'undefined') {
            newOpportunityData = {
              ...oppportunityAttributes,
              creatable_for: {
                ...creatable_for,
                // @ts-ignore
                data: { ...creatable_for.data, attributes: { ...creatable_for.data.attributes, following: record.creatable_for_following } },
              },
            };
          } else if (typeof record.creatable_for_subscription !== 'undefined') {
            newOpportunityData = {
              ...oppportunityAttributes,
              creatable_for: {
                ...creatable_for,
                // @ts-ignore
                data: { ...creatable_for.data, attributes: { ...creatable_for.data.attributes, ...record.creatable_for_subscription } },
              },
            };
          } else if (record.cohost_following) {
            newOpportunityData = {
              ...oppportunityAttributes,
              cohost_applications: {
                ...oppportunityAttributes.cohost_applications,
                data: oppportunityAttributes.cohost_applications.data.map((cohost_application) => {
                  if (
                    cohost_application.attributes.cohost_id === record.cohost_following?.cohostId ||
                    (record.cohost_following?.followingId &&
                      cohost_application.attributes.cohost.data.attributes.following?.data?.attributes.id === record.cohost_following.followingId)
                  ) {
                    return {
                      ...cohost_application,
                      attributes: {
                        ...cohost_application.attributes,
                        cohost: {
                          ...cohost_application.attributes.cohost,
                          data: {
                            ...cohost_application.attributes.cohost.data,
                            attributes: {
                              ...cohost_application.attributes.cohost.data.attributes,
                              following: record.cohost_following.following,
                            },
                          },
                        },
                      },
                    };
                  }
                  return cohost_application;
                }),
              },
            };
          } else if (record.cohost_subscription) {
            newOpportunityData = {
              ...oppportunityAttributes,
              cohost_applications: {
                ...oppportunityAttributes.cohost_applications,
                data: oppportunityAttributes.cohost_applications.data.map((cohost_application) => {
                  if (cohost_application.attributes.cohost_id === record.cohost_subscription?.cohostId) {
                    return {
                      ...cohost_application,
                      attributes: {
                        ...cohost_application.attributes,
                        cohost: {
                          ...cohost_application.attributes.cohost,
                          data: {
                            ...cohost_application.attributes.cohost.data,
                            attributes: {
                              ...cohost_application.attributes.cohost.data.attributes,
                              ...record.cohost_subscription.subscription,
                            },
                          },
                        },
                      },
                    };
                  }
                  return cohost_application;
                }),
              },
            };
          } else {
            newOpportunityData = { ...oppportunityAttributes, ...record };
          }

          return {
            ...oldOpportunityResponse,
            data: {
              ...oldOpportunityResponseData,
              data: {
                ...oldOpportunityResponseData.data,
                attributes: newOpportunityData,
              },
            },
          };
        },
        {
          revalidate: false,
        }
      );
    },
    [opportunityPath, opportunityResponse]
  );

  const handleShareURL = useCallback(() => {
    onShare(t('opportunity-page.url-copied'), public_url, { message: t('floating-action-btns.url-copied'), acknowledge: 'true' });
  }, []);

  const saveOpportunity = useCallback(async () => {
    if (isFollowing) return;
    try {
      setIsFollowLoading(true);
      const res = await followEntity({ followable_id: opportunityId, followable_type: 'Touchpoint' });
      if (res) {
        onUpdateOpportunity({ following: res });
        touchpointNotificationInstance({
          notificationType: NotificationType.SAVED_TOUCHPOINT,
          eventCount: saved_event_count,
          nonEventCount: saved_non_event_count,
        });
        if (isEvent) {
          updateCount({ saved_event_count: saved_event_count! + 1 });
        } else updateCount({ saved_non_event_count: saved_non_event_count! + 1 });
      }
    } catch (error) {
      console.error(error);
    } finally {
      updateUserAction({ userAction: null });
      setIsFollowLoading(false);
    }
  }, [opportunityId, isFollowing, followEntity, onUpdateOpportunity, updateUserAction]);

  const handleSaveOpportunity = useCallback(async () => {
    try {
      if (isCandidate) {
        await saveOpportunity();
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      console.error(error);
      updateUserAction({ userAction: null });
    }
  }, [isCandidate, saveOpportunity, signInModalInstance, updateUserAction]);

  const unsaveOpportunity = useCallback(async () => {
    if (!opportunityFollowingId) return;
    try {
      setIsFollowLoading(true);
      await unfollowEntity({ id: opportunityFollowingId });
      // @ts-ignore TODO: fix following type in Opportunity model
      onUpdateOpportunity({ following: null });
    } catch (error) {
      console.error(error);
    } finally {
      updateUserAction({ userAction: null });
      setIsFollowLoading(false);
    }
  }, [opportunityFollowingId, unfollowEntity, onUpdateOpportunity, updateUserAction]);

  const handleUnsaveOpportunity = useCallback(async () => {
    try {
      if (isCandidate) {
        if (!opportunityFollowingId) return;
        await unsaveOpportunity();
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      updateUserAction({ userAction: null });
    }
  }, [isCandidate, opportunityFollowingId, unfollowEntity, signInModalInstance]);

  const applyExternalJob = useCallback(() => {
    setTimeout(() => {
      window.open(apply_url, '_blank');
    });
    updateUserAction({ userAction: UserActionEnum.APPLY_CONFIRMATION });
  }, [apply_url, updateUserAction]);

  const handlePageRedirect = useCallback(async () => {
    try {
      if (isCandidate) {
        if (typeof queryActionUrl === 'string') {
          push(queryActionUrl);
        } else updateUserAction({ userAction: null });
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      console.error(error);
      updateUserAction({ userAction: null });
    }
  }, [isCandidate, queryActionUrl, signInModalInstance, updateUserAction]);

  const applyInternalJob = useCallback(async () => {
    if (internalJobApplicationInProgressRef.current) return;
    try {
      internalJobApplicationInProgressRef.current = true;
      const response = await registrationDetailsModalInstance({ touchpointId: opportunityId, isJoinWaitlist: willJoinWaitlist });
      if (response === FLAG_STATUS_CODES.NEW_USER_REGISTER_SUCCESS) {
        setIsNewCandidate(true);
        onUpdateOpportunity({ is_applied: true, is_waitlisted: willJoinWaitlist });
        if (isEvent && isOpportunityPublicPage) {
          touchpointNotificationInstance({
            notificationType: willJoinWaitlist ? NotificationType.INTERNAL_EVENT_WAITLIST : NotificationType.INTERNAL_EVENT_REGISTERED,
          });
          await nonOnboardedUserRegistrationSuccessModalInstance({
            opportunity,
            willJoinWaitlist: !!willJoinWaitlist,
            organizationName: creatableForName,
          });
        }
      } else if (response === FLAG_STATUS_CODES.EXISTING_USER_REGISTER_SUCCESS) {
        onUpdateOpportunity({ is_applied: true, is_waitlisted: willJoinWaitlist });
        if (isEvent) {
          touchpointNotificationInstance({
            notificationType: willJoinWaitlist ? NotificationType.INTERNAL_EVENT_WAITLIST : NotificationType.INTERNAL_EVENT_REGISTERED,
          });
        } else {
          touchpointNotificationInstance({
            notificationType: NotificationType.INTERNAL_JOB_APPLY,
            type: touchpointable_type,
          });
        }
      }
      if (response === FLAG_STATUS_CODES.NEW_USER_REGISTER_SUCCESS || response === FLAG_STATUS_CODES.EXISTING_USER_REGISTER_SUCCESS) {
        updateUserAction({ userAction: null });
        await handleNotifyUserModal();
      }
    } catch (error) {
      console.error(error);
    } finally {
      internalJobApplicationInProgressRef.current = false;
      updateUserAction({ userAction: null });
    }
  }, [
    isEvent,
    opportunity,
    opportunityId,
    willJoinWaitlist,
    creatableForName,
    touchpointable_type,
    isOpportunityPublicPage,
    onUpdateOpportunity,
    updateUserAction,
  ]);

  const applyJob = useCallback(async () => {
    if (!candidate) return;
    try {
      if (apply_url) {
        const { show_chrome_externsion_banner, show_complete_profile_banner, extension_installed, profile_complete } = candidate;
        const shouldOpenApplyURLAndGetConfirmation =
          (!show_chrome_externsion_banner && !show_complete_profile_banner) ||
          (!show_complete_profile_banner && extension_installed) ||
          (!show_chrome_externsion_banner && profile_complete) ||
          (extension_installed && profile_complete);

        if (shouldOpenApplyURLAndGetConfirmation) {
          applyExternalJob();
        } else {
          await externalApplyModalInstance({ apply_url });
          applyExternalJob();
        }
      } else {
        await applyInternalJob();
      }
    } catch (error) {
      console.error(error);
    } finally {
      updateUserAction({ userAction: null });
    }
  }, [candidate, opportunityId, apply_url, updateUserAction, applyExternalJob, applyInternalJob]);

  const trackClickOpportunity = useCallback(async () => {
    if (isCandidate || !isLoggedIn) {
      onApplyClick({ touchpointId: opportunityId });
    }
  }, [isLoggedIn, isCandidate, opportunityId, onApplyClick]);

  useEffect(() => {
    if (queryUserAction === UserActionEnum.APPLY || queryUserAction === UserActionEnum.REGISTER) {
      trackClickOpportunity();
    }
  }, [queryUserAction]);

  const handleNotifyUserModal = useCallback(async () => {
    try {
      if (creatableForFollowingId) return;
      await notifyUserModalInstance({ organizationName: creatableForName, creatableForType });
      if (creatableForType === 'company') {
        updateUserAction({ userAction: UserActionEnum.FOLLOW_COMPANY, userActionId: creatableForId });
      } else {
        updateUserAction({ userAction: UserActionEnum.JOIN_SOCIETY, userActionId: creatableForId });
      }
    } catch (error) {
      console.error(error);
    }
  }, [creatableForId, creatableForName, creatableForType, creatableForFollowingId, updateUserAction]);

  const handleApplyJob = useCallback(async () => {
    try {
      if (isCandidate) {
        await applyJob();
      } else {
        if (apply_url) {
          const responseCode = await externalApplySignupModalInstance({ touchpointable_type });
          if (responseCode === FLAG_STATUS_CODES.APPLY_ANYWAY) {
            applyExternalJob();
          } else if (responseCode === FLAG_STATUS_CODES.SIGN_IN_FLOW) {
            await signInModalInstance();
          }
        } else {
          await applyInternalJob();
        }
      }
    } catch (error) {
      updateUserAction({ userAction: null });
      handleNotifyUserModal();
    }
  }, [isCandidate, isLoggedIn, touchpointable_type, applyJob, applyExternalJob, handleNotifyUserModal, signInModalInstance, updateUserAction]);

  const applyConfimation = useCallback(async () => {
    try {
      const flag = await applyConfirmationModalInstance({ isEvent });
      if (flag === FLAG_STATUS_CODES.CONFIRM_APPLY_YES) {
        const response = await applyOpportunity(opportunityId);
        if (response) {
          onUpdateOpportunity({ is_applied: true });
          touchpointNotificationInstance({
            notificationType: NotificationType.EXTERNAL_APPLY,
            type: touchpointable_type,
            eventCount: external_registrated_event_count,
            nonEventCount: external_applied_non_event_count,
          });
          if (isEvent) {
            updateCount({
              external_registrated_event_count: external_registrated_event_count! + 1,
            });
            handleNotifyUserModal();
          } else {
            updateCount({
              external_applied_non_event_count: external_applied_non_event_count! + 1,
            });
          }
        }
      } else if (flag === FLAG_STATUS_CODES.CONFIRM_APPLY_INCORRECT_LINK) {
        sendInvalidURLReport({ id: opportunityId });
      }
    } catch (error) {
      console.error(error);
    } finally {
      updateUserAction({ userAction: null });
    }
  }, [opportunityId, isEvent, onUpdateOpportunity, updateUserAction, applyOpportunity, sendInvalidURLReport, handleNotifyUserModal]);

  const handleApplyConfirmation = useCallback(async () => {
    try {
      if (isCandidate) {
        await applyConfimation();
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      console.error(error);
      updateUserAction({ userAction: null });
    }
  }, [isCandidate, applyConfimation, signInModalInstance, updateUserAction]);

  const toggleFollowOrganization = useCallback(async () => {
    let organizationType,
      followRequest = null;
    switch (queryUserAction) {
      case UserActionEnum.FOLLOW_COMPANY:
        followRequest = true;
        organizationType = 'Company';
        break;
      case UserActionEnum.UNFOLLOW_COMPANY:
        followRequest = false;
        organizationType = 'Company';
        break;
      case UserActionEnum.JOIN_SOCIETY:
        followRequest = true;
        organizationType = 'StudentSociety';
        break;
      case UserActionEnum.DISJOIN_SOCIETY:
        followRequest = false;
        organizationType = 'StudentSociety';
        break;
      default:
        break;
    }

    try {
      if (typeof queryUserActionId !== 'string' || !organizationType || followRequest === null) return;
      setIsFollowLoading(true);
      if (followRequest) {
        if (organizationType === 'StudentSociety') {
          const joinCommunity = async () => {
            const response = await onJoinSociety({ email: candidate?.email || '', student_society_id: queryUserActionId });
            const { member, approved } = response;
            const record = member && approved ? { subscribed: true, awaiting_approval: false } : { subscribed: false, awaiting_approval: true };
            let societyName;
            if (creatableForId === queryUserActionId) {
              onUpdateOpportunity({ creatable_for_subscription: record });
              societyName = creatableForName;
            } else {
              onUpdateOpportunity({ cohost_subscription: { cohostId: queryUserActionId, subscription: record } });
              societyName = cohosts.find((item) => item.id === queryUserActionId)?.name || '';
            }
            touchpointNotificationInstance({
              notificationType: NotificationType.SOCIETY_JOIN,
              followCount: student_society_follow_count,
              name: societyName,
              approvalReq: !(member && approved),
            });
            updateCount({
              student_society_follow_count: student_society_follow_count! + 1,
            });
          };

          if (first_name && last_name) {
            joinCommunity();
          } else {
            let societyName, approvalRequired;
            if (creatableForId === queryUserActionId) {
              societyName = creatableForName;
              approvalRequired = subscribers_require_approval;
            } else {
              const cohostSociety = cohosts.find((item) => item.id === queryUserActionId);
              societyName = cohostSociety?.name || '';
              approvalRequired = cohostSociety?.subscribers_require_approval;
            }

            const data = await joinMemberModalInstance({
              societyName: societyName,
              isPrivateSociety: approvalRequired,
            });
            if (data || !approvalRequired) {
              joinCommunity();
            }
          }
        } else {
          const response = await followEntity({ followable_type: organizationType, followable_id: queryUserActionId });
          if (creatableForId === queryUserActionId) {
            onUpdateOpportunity({ creatable_for_following: response });
          } else {
            onUpdateOpportunity({ cohost_following: { cohostId: queryUserActionId, following: response } });
          }
          touchpointNotificationInstance({
            notificationType: NotificationType.COMPANY_FOLLOW,
            followCount: company_follow_count,
            name: creatableForName,
          });
          updateCount({ company_follow_count: company_follow_count! + 1 });
        }
      } else {
        if (organizationType === 'StudentSociety') {
          await onDisjoinSociety({ id: queryUserActionId });
          const record = { subscribed: false, awaiting_approval: false };
          if (creatableForId === queryUserActionId) {
            onUpdateOpportunity({ creatable_for_subscription: record });
          } else {
            onUpdateOpportunity({ cohost_subscription: { cohostId: queryUserActionId, subscription: record } });
          }
        } else {
          await unfollowEntity({ id: queryUserActionId });
          if (creatableForFollowingId === queryUserActionId) {
            onUpdateOpportunity({ creatable_for_following: null });
          } else {
            onUpdateOpportunity({ cohost_following: { followingId: queryUserActionId, following: null } });
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      updateUserAction({ userAction: null });
      setIsFollowLoading(false);
    }
  }, [
    candidate,
    cohosts,
    queryUserAction,
    queryUserActionId,
    creatableForId,
    creatableForName,
    subscribers_require_approval,
    creatableForFollowingId,
    awaitingApproval,
    followEntity,
    updateUserAction,
    unfollowEntity,
    onUpdateOpportunity,
    onJoinSociety,
    onDisjoinSociety,
  ]);

  const handleToggleFollowingOrganization = useCallback(async () => {
    try {
      if (isCandidate) {
        await toggleFollowOrganization();
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      console.error(error);
      updateUserAction({ userAction: null });
    }
  }, [isCandidate, toggleFollowOrganization, updateUserAction, signInModalInstance]);

  useEffect(() => {
    if (isInitialAuthLoading) return;
    if (isCandidate && opportunityResponse?.status !== 200 && shouldFetchOpportunity) return;

    if (Object.values(UserActionEnum).includes(queryUserAction as UserActionEnum)) {
      switch (queryUserAction) {
        case UserActionEnum.SAVE:
          handleSaveOpportunity();
          break;
        case UserActionEnum.UNSAVE:
          handleUnsaveOpportunity();
          break;
        case UserActionEnum.APPLY:
        case UserActionEnum.REGISTER:
          handleApplyJob();
          break;
        case UserActionEnum.APPLY_CONFIRMATION:
          handleApplyConfirmation();
          break;
        case UserActionEnum.FOLLOW_COMPANY:
        case UserActionEnum.UNFOLLOW_COMPANY:
        case UserActionEnum.JOIN_SOCIETY:
        case UserActionEnum.DISJOIN_SOCIETY:
          handleToggleFollowingOrganization();
          break;
        case UserActionEnum.REDIRECT_TO:
          handlePageRedirect();
          break;
        default:
          break;
      }
    }
  }, [isInitialAuthLoading, isCandidate, opportunityResponse, shouldFetchOpportunity, queryUserAction]);

  const aiFaqs = useMemo(() => {
    return parseOpportunityAiFaqs(opportunity.ai_faqs);
  }, [opportunity]);

  useEffect(() => {
    async function incrementViewCount() {
      try {
        const path = `${API_ROUTES.TOUCHPOINTS}/${opportunityId}/increment_view_count`;
        const body = {};
        await apiInstance.patch(path, body);
      } catch (error) {
        console.error(error);
      }
    }
    incrementViewCount();
  }, [opportunityId]);

  useEffect(() => {
    if (!isOpportunityPublicPage) return;

    const isCompanysExternalOpporunity = creatableForType === 'company' && apply_url;
    if (isCandidate && isNonOnboardedUser && isCompanysExternalOpporunity && !queryUserAction) {
      replace(PAGE_ROUTES.CANDIDATE_ONBOARDING);
    }
  }, [isCandidate, creatableForType, apply_url, isNonOnboardedUser, queryUserAction, isOpportunityPublicPage]);

  return {
    opportunity,
    isOpportunityLoading,
    isFollowLoading,
    isFollowing,
    isExpired,
    isApplicationClosed,
    isEventEnded,
    isAlmostSoldOut,
    isApprovalRequired,
    isWaitlistEnabled,
    isCapacityReached,
    isOpportunityUnappliable,
    isOpportunityAppliedOrUnappliable,
    aiFaqs,
    willJoinWaitlist,
    queryUserAction,
    queryUserActionId,
    creatableFor,
    creatableForId,
    creatableForName,
    creatableForType,
    creatableForFollowingId,
    awaitingApproval,
    similarOpportunities,
    similarOpportunitiesLoading,
    isOpportunityPublicPage,
    isMatchesPage,
    matchSlug,
    isSearchPage,
    isPageFullyScrolled,
    handleShareURL,
    updateUserAction,
  };
}

export type UseOpportunityReturnType = ReturnType<typeof useOpportunityInternalHook>;
